import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStudent } from "../../../../../context/StudentLoginContext/StudentAuthContext";
import { Modal } from "react-bootstrap";
import "./player.css";

const JwPlayerMultiLanguage = ({
  videoSources,
  nextEpisodeId,
  videoStartTime,
  chapters,
  type,
  isPaused,
  setIsPaused
}) => {
  const defaultSources = {
    en: "https://cdn.jwplayer.com/manifests/RbbzytoU.m3u8",
    hi: "https://cdn.jwplayer.com/manifests/RbbzytoU.m3u8",
  };
  const validSources =
    videoSources && typeof videoSources === "object"
      ? videoSources
      : defaultSources;
  const [language, setLanguage] = useState("en");
  const [prevlanguage, setPrevLanguage] = useState("en");
  const [playerInstance, setPlayerInstance] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const { student,fetchWithAuthStudent } = useAuthStudent();
  const { questId, chapterId } = useParams();
  const videoTimeStamp = Number(
    chapters?.remaining_time?.videoTimeStamp || 0
  );
  const videoCompleteTime = Number(
    chapters?.remaining_time?.videoCompleteTime || 1
  );
  const [watchedPercentage] = useState(
    (videoTimeStamp / videoCompleteTime) * 100
  );
  const playerContainerRef = useRef(null);

  const isDataReady =
    student?.student?.id && questId && chapterId && validSources[language];

  // Show modal if video is already completed
  useEffect(() => {
    if (chapters?.remaining_time?.videoCompleteStatus === "1") {
      setShowModal(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  // JWPlayer script loader and player setup with fullscreen/scroll fix
  useEffect(() => {
    if (!isDataReady) return;

    const playerContainer = playerContainerRef.current; // Fix: copy ref value

    setErrorMsg(""); // Reset error

    // Exit fullscreen if active before destroying player
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (document.webkitFullscreenElement) {
      document.webkitExitFullscreen();
    } else if (document.mozFullScreenElement) {
      document.mozCancelFullScreen();
    } else if (document.msFullscreenElement) {
      document.msExitFullscreen();
    }

    // Remove any previous player
    if (window.jwplayer && window.jwplayer("jw-player")) {
      try {
        window.jwplayer("jw-player").remove();
      } catch (e) {}
    }

    // Remove previous overlays
    if (playerContainer) {
      playerContainer.innerHTML = "";
    }

    // Delay setup to allow cleanup
    const timeoutId = setTimeout(() => {
      // Restore scrollbars in case they were hidden
      document.body.style.overflow = "auto";

      // Load JWPlayer script if not already loaded
      const scriptId = "jwplayer-script";
      let script = document.getElementById(scriptId);
      let scriptLoaded = !!window.jwplayer;

      const setupPlayer = () => {
        if (!window.jwplayer) {
          setErrorMsg("Video player failed to load. Please refresh the page.");
          return;
        }

        // Remove any previous player
        if (window.jwplayer("jw-player")) {
          try {
            window.jwplayer("jw-player").remove();
          } catch (e) {}
        }

        // Create player container
        if (playerContainer) {
          playerContainer.innerHTML = '<div id="jw-player"></div>';
        }

        const localizationOptions = {
          en: { menu: "Menu", play: "Play", pause: "Pause" },
          hi: { menu: "मेनू", play: "चलाएं", pause: "रोकें" },
        };

        const player = window.jwplayer("jw-player").setup({
          file: validSources[language],
          width: "100%",
          aspectratio: "16:9",
          autostart: "viewable",
          mute: false,
          controls: true,
          preload: "auto",
          localization: localizationOptions[language],
        });

        // Error handler
        player.on("error", (err) => {
          setErrorMsg(
            "Video failed to load. Please check your internet connection or try a different browser/device."
          );
        });

        // Add forward button
        player.on("ready", () => {
          // player.addButton(
          //   `${VideoForwardIcn}`,
          //   "Forward 10s",
          //   () => {
          //     const currentTime = player.getPosition();
          //     const duration = player.getDuration();
          //     player.seek(Math.min(currentTime + 10, duration));
          //   },
          //   "forward-button"
          // );

          // Overlay for language selection
          const container = document.querySelector("#jw-player");
          if (!container) return;
          container.style.position = "relative";

          const overlay = document.createElement("div");
          overlay.style.position = "absolute";
          overlay.style.color = "#ffffff";
          overlay.style.cursor = "pointer";
          overlay.style.fontFamily = "sans-serif";
          overlay.classList.add("language-overlay");

          const displayText = document.createElement("div");
          displayText.textContent = "Language";
          displayText.style.border = "1px solid #80CAFF";
          displayText.style.borderRadius = "4px";
          displayText.style.background = "rgba(0, 0, 0, 0.6)";
          displayText.style.display = "inline-block";
          displayText.classList.add("font-pad");
          const dropdown = document.createElement("div");
          dropdown.style.position = "absolute";
          dropdown.style.bottom = "30px";
          dropdown.style.right = "0";
          dropdown.style.background = "rgba(0, 0, 0, 0.8)";
          dropdown.style.border = "1px solid #80CAFF";
          dropdown.style.borderRadius = "4px";
          dropdown.style.display = "none";
          dropdown.style.flexDirection = "column";
          dropdown.style.minWidth = "100px";

          const languages = [
            { value: "en", label: "English" },
            { value: "hi", label: "Hindi" },
          ];

          languages.forEach(({ value, label }) => {
            const option = document.createElement("div");
            option.textContent = label;
            option.style.padding = "5px 10px";
            option.style.cursor = "pointer";
            option.style.color = "#80CAFF";
            option.addEventListener("click", () => {
              setPrevLanguage(language);
              setLanguage(value);
              localStorage.setItem("language", value);
              dropdown.style.display = "none";
            });
            dropdown.appendChild(option);
          });

          displayText.addEventListener("click", (e) => {
            e.stopPropagation();
            dropdown.style.display =
              dropdown.style.display === "none" ? "flex" : "none";
          });

          document.addEventListener(
            "click",
            (e) => {
              if (!overlay.contains(e.target)) {
                dropdown.style.display = "none";
              }
            },
            { once: true }
          );

          overlay.appendChild(displayText);
          overlay.appendChild(dropdown);
          container.appendChild(overlay);

          let hideTimeout;

          const showOverlay = () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            overlay.style.opacity = "1";
          };

          const scheduleHideOverlay = () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = setTimeout(() => {
              overlay.style.opacity = "0";
              dropdown.style.display = "none";
            }, 2000);
          };

          // Always show overlay on mousemove, then auto-hide
          container.addEventListener("mousemove", () => {
            showOverlay();
            scheduleHideOverlay();
          });

          // Also hook into JW Player's fullscreen change
          player.on("fullscreen", (event) => {
            if (event.fullscreen) {
              // In fullscreen: use mousemove-based show/hide
              document.addEventListener("mousemove", handleFullscreenMouseMove);
            } else {
              // Exit fullscreen: clean up
              document.removeEventListener(
                "mousemove",
                handleFullscreenMouseMove
              );
            }
          });

          function handleFullscreenMouseMove() {
            showOverlay();
            scheduleHideOverlay();
          }

          player.on("useractive", showOverlay);
          // player.on("userinactive", hideOverlay);
          container.addEventListener("mouseenter", showOverlay);
          // container.addEventListener("mouseleave", scheduleHideOverlay);

          // Scroll player into view after language change
          if (playerContainer) {
            playerContainer.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });

        player.on("time", (event) => setCurrentTime(event.position));

        player.on("ready", () => {
          if (prevlanguage !== language && currentTime > 0) {
            player.seek(currentTime);
          } else {
            if (watchedPercentage === 100) {
              player.seek(0);
              setShowModal(true);
            } else if (Number(videoStartTime) >= 0) {
              player.seek(videoStartTime);
            }
          }
        });

        player.on("pause", () => {
          if (!student?.student?.id || !questId || !chapterId) return;
          sendVideoData({
            studentId: String(student.student.id),
            questId: String(questId),
            chapterId: String(chapterId),
            videoLeftTime: String(player.getDuration() - player.getPosition()),
            videoCompleteTime: String(player.getDuration()),
            videoCompleteStatus: "0",
            videoTimeStamp: String(player.getPosition()),
          });
          setIsPaused(true)
        });
        player.on("play", () => {
          if (!student?.student?.id || !questId || !chapterId) return;
          setIsPaused(false)
        });
        player.on("complete", async () => {
          const duration = player.getDuration();
          sendVideoData({
            studentId: String(student.student.id),
            questId: String(questId),
            chapterId: String(chapterId),
            videoTimeStamp: String(duration),
            videoCompleteTime: String(duration),
            videoCompleteStatus: "1",
          });

          let fetchedQuizzes = [];
          try {
            localStorage.removeItem("chapter");
            const response = await fetchWithAuthStudent(
              `${apiUrl}/student/quest-chapter-mcq/${chapterId}`,
              {
                method: "GET",
              }
            );

            if (!response.ok) throw new Error("Failed to fetch quizzes");

            const data = await response.json();
            fetchedQuizzes = data.map((item) => ({
              question: item.question,
              options: [
                item.option1,
                item.option2,
                item.option3,
                item.option4,
              ].filter(Boolean),
              correctAnswer: item.correctAnswer,
              id: item.id,
            }));
          } catch (error) {}

          if (fetchedQuizzes.length > 0 && nextEpisodeId === null) {
            if (type === "quest") {
              navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
            } else {
              navigate(
                `/student/admin/quiz/festive/begin/${questId}/${chapterId}`
              );
            }
            localStorage.setItem("chapter", "end");
          } else if (fetchedQuizzes.length > 0) {
            if (type === "quest") {
              navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
            } else {
              navigate(
                `/student/admin/quiz/festive/begin/${questId}/${chapterId}`
              );
            }
            localStorage.removeItem("chapter");
          } else if (nextEpisodeId === null) {
            sendAwardTrophy({
              studentId: String(student.student.id),
              questId: String(questId),
            });
            navigate(`/student/admin/episodes/${questId}`);
            localStorage.removeItem("chapter");
          } else {
            navigate(
              `/student/admin/episodes/detail/${questId}/${nextEpisodeId}`
            );
            localStorage.removeItem("chapter");
          }
        });

        setPlayerInstance(player);
      };

      if (!scriptLoaded) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://cdn.jwplayer.com/libraries/8LwdHpsM.js";
        script.async = true;
        script.onload = setupPlayer;
        script.onerror = () => {
          setErrorMsg("Failed to load video player. Please try again later.");
        };
        document.body.appendChild(script);
      } else {
        setupPlayer();
      }
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (window.jwplayer && window.jwplayer("jw-player")) {
        try {
          window.jwplayer("jw-player").remove();
        } catch (e) {}
      }
      if (playerContainer) {
        playerContainer.innerHTML = "";
      }
      // Restore scrollbars on cleanup
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line
  }, [language, isDataReady]);

  // Send video progress
  const sendVideoData = (data) => {
    if (!student?.student?.id) return;
    fetchWithAuthStudent(`${apiUrl}/student/video-progress/store`, {
      method: "POST",
      body: JSON.stringify(data),
    }).then((res) => res.json());
  };

  // Award trophy
  const sendAwardTrophy = (data) => {
    if (!student?.student?.id) return;
    fetchWithAuthStudent(`${apiUrl}/student/award-trophy`, {
      method: "POST",
      body: JSON.stringify(data),
    }).then((res) => res.json());
  };

  // Modal actions
  const handleQuiz = () => {
    if (nextEpisodeId === null) {
      localStorage.setItem("chapter", "end");
    } else {
      localStorage.removeItem("chapter");
    }
    if (type === "quest") {
      navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
    } else {
      navigate(`/student/admin/quiz/festive/begin/${questId}/${chapterId}`);
    }
    setShowModal(false);
  };

  const handleReplay = () => {
    if (playerInstance) {
      playerInstance.seek(0);
      setShowModal(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <select
        onChange={(e) => {
          setPrevLanguage(language);
          setLanguage(e.target.value);
        }}
        value={language}
        className="VideoLangSlctr"
      >
        <option disabled>Language</option>
        <option value="en">English</option>
        <option value="hi">Hindi</option>
      </select>

      <div ref={playerContainerRef}></div>

      {errorMsg && (
        <div className="alert alert-danger mt-3" role="alert">
          {errorMsg}
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>You’ve nearly finished the video!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Would you like to replay the video from the beginning or attempt the
          quiz?
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleReplay}>
            Replay from Start
          </button>
          <button className="btn btn-primary" onClick={handleQuiz}>
            Attempt Quiz
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

JwPlayerMultiLanguage.propTypes = {
  videoSources: PropTypes.object,
};

export default JwPlayerMultiLanguage;
